import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import { Channel } from '@/Enums/Channel.enum';
import CreativeFileStatus from '@/Enums/Creatives/CreativeFileStatus.enum';
import CreativeMaxDuration from '@/Enums/Creatives/CreativeMaxDuration.enum';
import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import CreativesTableColumns from '@/Enums/Creatives/CreativesTableColumns.enum';
import FileExtension from '@/Enums/FileExtension.enum';
import ItemStatus from '@/Enums/ItemStatus.enum';
import MimeType from '@/Enums/MimeType.enum';
import HeadsetIcon from '@/components/Icons/HeadsetIcon';
import OlvIcon from '@/components/Icons/OlvIcon';
import SmartTvIcon from '@/components/Icons/SmartTvIcon';
import { kilobytesToBytes, megabytesToBytes } from '@/helpers/byteValueConverter.helpers';
import { Code } from '@mui/icons-material';
import type { ElementType } from 'react';

const CreativeMaxFileSize = Object.freeze({
  Image: kilobytesToBytes(400),
  Html: megabytesToBytes(2),
  Video: megabytesToBytes(245),
  Audio: Math.floor(megabytesToBytes(2.8)),
});

const CreativeMaxImageDimensions = Object.freeze({
  width: 4096,
  height: 4096,
});

export const validFormats = {
  [CreativeType.DISPLAY]: {
    acceptedExtensions: [FileExtension.JPEG, FileExtension.JPG, FileExtension.PNG, FileExtension.GIF],
    acceptedTypes: [MimeType.IMAGE_JPEG, MimeType.IMAGE_PNG, MimeType.IMAGE_GIF],
    maxFileSize: CreativeMaxFileSize.Image,
    maxWidth: CreativeMaxImageDimensions.width,
    maxHeight: CreativeMaxImageDimensions.height,
  },
  [CreativeType.HTML5]: {
    acceptedExtensions: [FileExtension.HTML],
    acceptedTypes: [MimeType.TEXT_HTML],
    maxFileSize: CreativeMaxFileSize.Html,
  },
  [CreativeType.VIDEO]: {
    acceptedExtensions: [FileExtension.MP4, FileExtension.MOV],
    acceptedTypes: [MimeType.VIDEO_MP4, MimeType.VIDEO_QUICKTIME],
    maxDuration: CreativeMaxDuration.Video,
    maxFileSize: CreativeMaxFileSize.Video,
  },
  [CreativeType.AUDIO]: {
    acceptedExtensions: [FileExtension.MP3],
    acceptedTypes: [MimeType.AUDIO_MP3],
    maxDuration: CreativeMaxDuration.Audio,
    maxFileSize: CreativeMaxFileSize.Audio,
  },
};

export const CREATIVE_TYPES_TITLES: Record<CreativeType, string> = {
  [CreativeType.VIDEO]: 'Video',
  [CreativeType.VIDEO_VAST]: 'Video VAST',
  [CreativeType.DISPLAY]: 'Display',
  [CreativeType.DISPLAY_TAG]: 'Display Tag',
  [CreativeType.HTML5]: 'HTML5',
  [CreativeType.AUDIO]: 'Audio',
  [CreativeType.AUDIO_VAST]: 'Audio VAST',
  [CreativeType.YOUTUBE_LINK]: 'YouTube Link',
};

export const CREATIVE_STATUSES_TITLES = {
  [ItemStatus.ACTIVE]: 'Active',
  [ItemStatus.INACTIVE]: 'Inactive',
};

export const CREATIVE_TYPES_ICONS: Record<CreativeType, ElementType> = {
  [CreativeType.VIDEO]: OlvIcon,
  [CreativeType.VIDEO_VAST]: Code,
  [CreativeType.DISPLAY]: SmartTvIcon,
  [CreativeType.HTML5]: Code,
  [CreativeType.DISPLAY_TAG]: Code,
  [CreativeType.AUDIO]: HeadsetIcon,
  [CreativeType.AUDIO_VAST]: Code,
  [CreativeType.YOUTUBE_LINK]: Code,
};

export const CREATIVE_ASSIGNMENT_DISABLED_REASON = {
  [CreativeFileStatus.PROCESSING]: (
    <span>
      Please wait for the file to be processed <br /> Use Refresh button to see updates
    </span>
  ),
  [CreativeFileStatus.FAILED]: (
    <span>
      File upload failed for this creative <br /> Please try reuploading it
    </span>
  ),
  null: (
    <span>
      File upload failed for this creative <br /> Please try reuploading it
    </span>
  ),
  noBeeswaxId: <span>Processing</span>,
};

export const CREATIVE_STATUSES = [ItemStatus.ACTIVE, ItemStatus.INACTIVE] as const;

export const CREATIVE_TYPES_FOR_CHANNEL = {
  [Channel.CTV]: [CreativeType.VIDEO, CreativeType.VIDEO_VAST],
  [Channel.OLV]: [CreativeType.VIDEO, CreativeType.VIDEO_VAST],
  [Channel.Display]: [CreativeType.DISPLAY, CreativeType.DISPLAY_TAG, CreativeType.HTML5],
  [Channel.Audio]: [CreativeType.AUDIO, CreativeType.AUDIO_VAST],
};

export const BASE_TAB_ID = 'creative-filter-tab';
export const CLIENTS_TAB_INDEX = 0;
export const ADVERTISERS_TAB_INDEX = 1;
export const CAMPAIGNS_TAB_INDEX = 2;

export const DEFAULT_CREATIVE_FILTERS = {
  types: [],
  statuses: [],
  whitelabels: [],
  clients: [],
  advertisers: [],
  page: 1,
  limit: 15,
  search: '',
  sort: null,
  platform: CampaignPlatformType.Omnichannel,
  isArchived: false,
};

export const CREATIVE_IN_USE_ARCHIVE_DISABLED_REASON = 'Used by a line item, so it cannot be archived';

export const CREATIVES_DEFAULT_SORT = { column: CreativesTableColumns.UPDATED_AT, order: 'desc' };
